import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { DataTable, type Column } from '@/components/DataTable';
import { OutboundStatusBadge, outboundStatusLabel } from '@/components/StatusBadge';
import { ListFilterBar } from '@/components/ListFilterBar';
import { useListFilters } from '@/lib/useListFilters';
import { filterOptions } from '@/lib/filterOptions';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import { useDraftActions } from '@/lib/drafts';
import { describeRequestError } from '@/lib/requests';
import { ErrorBanner } from '@/components/ErrorBanner';
import { OnboardingNotice, NewRequestAction } from '@/components/OnboardingNotice';
import type { OrderRow } from '@/api/types';

const LIMIT = 50;
const FILTERS = ['DRAFT', 'NEW', 'RESERVED', 'PICKING', 'PACKED', 'SHIPPED', 'CANCELLED'] as const;

/**
 * "Отгрузки" - the ONE outbound section: every request/order the seller has (drafts, sent to the warehouse,
 * reserved, picking, packed, shipped, cancelled) with its friendly status and, once there is one, the carrier /
 * tracking of the shipment. It is a read-side aggregation over the existing order + shipment data; the backend
 * entities stay separate.
 *
 * `portalOrders` deliberately never joins `channel_credential` or any adapter-internal metadata (ADR-075 §Part
 * B.4) - `channel_kind` is the only channel fact rendered. Drafts carry quick actions (edit / send / delete);
 * a sent request has none here (it is kept in the history - cancel lives on its page).
 */
export function OrdersListPage(): JSX.Element {
  const [offset, setOffset] = useState(0);
  const list = useListFilters(() => setOffset(0));
  const result = useApi(() => endpoints.orders({ ...list.query, limit: LIMIT, offset }), [list.key, offset]);
  const options = useApi(() => endpoints.requestOptions(), []);
  const { t, locale } = useLanguage();
  const opts = filterOptions(options.status === 'success' ? options.data.owners : []);
  const reload = result.reload;
  const onChanged = useCallback(() => reload(), [reload]);
  const actions = useDraftActions('OUTBOUND', onChanged);

  const columns: readonly Column<OrderRow>[] = [
    { key: 'number', header: t('out.colNumber'), render: (r) => <>{r.number}{(r.revision_no ?? 1) > 1 && <span className="revision-mark" title={t('revise.editedBadge')}>{t('revise.editedMark')}</span>}</> },
    ...(opts.owners.length > 1 ? [{ key: 'client', header: t('filters.client'), render: (r: { owner_name?: string }) => r.owner_name ?? '—' }] : []),
    { key: 'state', header: t('common.status'), render: (r) => <OutboundStatusBadge value={r.state} /> },
    { key: 'warehouse', header: t('requests.warehouse'), render: (r) => r.warehouse_name ?? '—' },
    { key: 'ordered', header: t('ordersPage.colOrdered'), align: 'right', render: (r) => formatNumber(r.qty_ordered, locale) },
    { key: 'shipped', header: t('ordersPage.colShipped'), align: 'right', render: (r) => formatNumber(r.qty_shipped, locale) },
    {
      key: 'carrier',
      header: t('out.colCarrier'),
      render: (r) => (r.carrier_kind || r.carrier_ref ? [r.carrier_kind, r.carrier_ref].filter(Boolean).join(' · ') : '—'),
    },
    { key: 'created', header: t('requests.in.colCreated'), render: (r) => formatDateTime(r.created_at, locale) },
    {
      key: 'actions',
      header: t('drafts.actions'),
      render: (r) =>
        r.state === 'DRAFT' && r.requested_in_portal ? (
          // a click on an action must not also open the row
          <span className="row-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <Link className="button-link" to={`/shipments/${r.id}/edit`} aria-label={`${t('drafts.edit')} ${r.number}`}>{t('drafts.edit')}</Link>
            <button type="button" className="primary" disabled={actions.busyId === r.id} onClick={() => void actions.send(r.id)} aria-label={`${t('drafts.send')} ${r.number}`}>
              {actions.busyId === r.id ? t('drafts.sending') : t('drafts.send')}
            </button>
            <button type="button" className="danger" disabled={actions.busyId === r.id} onClick={() => void actions.remove(r.id)} aria-label={`${t('drafts.delete')} ${r.number}`}>
              {t('drafts.delete')}
            </button>
          </span>
        ) : null,
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('nav.orders')}</h1>
        <NewRequestAction to="/shipments/new" label={t('requests.out.new')} />
      </div>
      <OnboardingNotice />
      <ListFilterBar
        filters={list.filters}
        onChange={list.set}
        onReset={list.reset}
        filtered={list.filtered}
        statuses={FILTERS.map((s) => ({ value: s, label: outboundStatusLabel(t, s) }))}
        owners={opts.owners}
        warehouses={opts.warehouses}
      />
      {actions.error !== null && (
        describeRequestError(t, actions.error)
          ? <div className="error-banner" role="alert">{describeRequestError(t, actions.error)}</div>
          : <ErrorBanner error={actions.error} />
      )}
      <DataTable
        result={result}
        columns={columns}
        getRowHref={(r) => `/shipments/${r.id}`}
        limit={LIMIT}
        offset={offset}
        onPageChange={setOffset}
        emptyTitle={list.filtered ? t('filters.noMatch') : t('ordersPage.emptyTitle')}
        emptyHint={list.filtered ? undefined : t('out.emptyHint')}
      />
    </div>
  );
}
